import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { compareByCategory, MENU_SECTIONS, sortCategories } from '@kortex/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { date, money, money0, pct, qty } from '../lib/format';
import { PageHeader } from '../components/Layout';
import { MenuItemImage } from '../components/MenuItemImage';
import {
  ConfirmDialog,
  Drawer,
  Empty,
  ErrorState,
  Kpi,
  Loading,
  Meter,
  Modal,
  Select,
  StatusChip,
  TextInput,
  useToast,
} from '../components/ui';

interface MenuRow {
  id: string;
  sku: string;
  name: string;
  categoryId: string;
  category: string;
  price: number;
  gstRate: number;
  hsn: string;
  station: string;
  isVegetarian: boolean;
  discountPct: number;
  isActive: boolean;
  hasImage: boolean;
  imageUrl: string | null;
  plateCost: number;
  foodCostPct: number;
  margin: number;
  marginPct: number;
  suggestedPrice: number;
  delta: number;
  flag: string;
  componentCount: number;
}

interface MenuDetail extends MenuRow {
  recipe: Array<{
    ingredientId: string;
    name: string;
    unit: string;
    quantity: number;
    unitCost: number;
    lineCost: number;
  }>;
  priceHistory: Array<{
    id: string;
    oldPrice: number;
    newPrice: number;
    reason: string;
    changedAt: string;
  }>;
}

interface Category {
  id: string;
  name: string;
}

/** The fields an owner can edit on an item; price and on-sale are separate. */
interface EditForm {
  name: string;
  categoryId: string;
  station: string;
  isVegetarian: boolean;
  gstRate: number;
  hsn: string;
  discountPct: number;
}

const STATIONS = ['KITCHEN', 'GRILL', 'FRYER', 'OVEN', 'BEVERAGE', 'BAKERY'] as const;
const titleCase = (value: string) => value[0] + value.slice(1).toLowerCase();

interface Ingredient {
  id: string;
  name: string;
  unit: string;
  avgCost: number;
}

export function MenuPage() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [bulk, setBulk] = useState(false);
  const [creating, setCreating] = useState(false);
  const [addingSection, setAddingSection] = useState(false);
  const [starter, setStarter] = useState(false);
  /*
   * Offered once, on a menu that has nothing on it.
   *
   * A hotel registers and lands here with an empty screen now that nothing is
   * created for them, and "write a hundred dishes" is not a first task
   * anybody wants. Offering the list once is help; re-opening it every time
   * they come back to an empty menu would be nagging, so a dismissal sticks
   * for the session and the button is always there.
   */
  const [offeredStarter, setOfferedStarter] = useState(false);

  const menu = useQuery({
    queryKey: ['menu', 'all'],
    queryFn: () => api.get<MenuRow[]>('/api/menu'),
  });

  // Nothing on the menu and nobody has been asked yet: offer the list.
  useEffect(() => {
    if (menu.isLoading || starter || offeredStarter) return;
    if ((menu.data?.length ?? 0) === 0 && can('menu:manage')) setStarter(true);
  }, [menu.isLoading, menu.data, starter, offeredStarter, can]);
  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/api/categories'),
  });
  const settings = useQuery({
    queryKey: ['property'],
    queryFn: () => api.get<{ settings: { targetFoodCostPct: number } }>('/api/property'),
  });

  const target = settings.data?.settings.targetFoodCostPct ?? 32;

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (menu.data ?? []).filter(
      (item) =>
        (category === 'All' || item.category === category) &&
        (term === '' ||
          item.name.toLowerCase().includes(term) ||
          item.sku.toLowerCase().includes(term)),
    );
  }, [menu.data, category, search]);

  if (menu.isLoading) return <Loading label="Costing the menu…" />;
  if (menu.error) return <ErrorState error={menu.error} onRetry={() => void menu.refetch()} />;

  const all = menu.data ?? [];
  // Averaged over priced items only: an unpriced item has a food cost of 0%,
  // and counting those drags the average toward zero and reads as a triumph.
  const priced = rows.filter((row) => row.price > 0);
  const avgFoodCost = priced.length
    ? priced.reduce((sum, r) => sum + r.foodCostPct, 0) / priced.length
    : 0;
  const unpriced = all.filter((row) => row.price <= 0);
  const lowMargin = all.filter((r) => r.flag === 'LOW_MARGIN');
  const noRecipe = all.filter((r) => r.flag === 'NO_RECIPE');

  return (
    <div className="stack">
      <PageHeader
        title="Menu & Pricing"
        subtitle="Recipe costing, margin control and auditable price revisions"
        actions={
          can('menu:price') ? (
            <>
              <button className="btn" onClick={() => setBulk(true)}>
                Bulk reprice
              </button>
              {can('menu:manage') ? (
                <>
                  <button className="btn" onClick={() => setStarter(true)}>
                    Starter menu
                  </button>
                  <button className="btn" onClick={() => setAddingSection(true)}>
                    Sections
                  </button>
                  <button className="btn primary" onClick={() => setCreating(true)}>
                    + New item
                  </button>
                </>
              ) : null}
            </>
          ) : null
        }
      />

      <div className="kpis">
        <Kpi
          label="Menu items"
          value={all.length}
          foot={`${categories.data?.length ?? 0} categories`}
        />
        <Kpi
          label="Average food cost"
          value={priced.length ? pct(avgFoodCost) : '—'}
          foot={
            priced.length
              ? `Target ${pct(target)} · ${priced.length} priced item${priced.length === 1 ? '' : 's'}`
              : 'Nothing priced yet'
          }
          tone={priced.length && avgFoodCost > target ? 'amber' : ''}
        />
        <Kpi
          label="Below margin"
          value={lowMargin.length}
          foot="More than 8 points over target"
          tone={lowMargin.length ? 'red' : ''}
        />
        <Kpi
          label={noRecipe.length ? 'Missing a recipe' : 'Waiting on a price'}
          value={noRecipe.length ? noRecipe.length : unpriced.length}
          foot={noRecipe.length ? 'Cannot be costed yet' : 'Costed, but not on sale until priced'}
          tone={noRecipe.length || unpriced.length ? 'amber' : ''}
        />
      </div>

      <div className="card">
        <div className="card-head">
          <div className="tabs">
            {['All', ...sortCategories((categories.data ?? []).map((c) => c.name))].map((name) => (
              <button
                key={name}
                className={`tab ${name === category ? 'active' : ''}`}
                onClick={() => setCategory(name)}
              >
                {name}
              </button>
            ))}
          </div>
          <div className="search" style={{ width: 210 }}>
            <input
              className="input"
              placeholder="Search item"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {rows.length === 0 ? (
          <Empty message="No items match" />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Category</th>
                  <th className="num">Price</th>
                  <th className="num">Discount</th>
                  <th className="num">Plate cost</th>
                  <th className="num">Food cost</th>
                  <th className="num">Margin</th>
                  <th className="num">Suggested</th>
                  <th>Status</th>
                  <th>On sale</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <tr key={item.id} className="clickable" onClick={() => setOpenId(item.id)}>
                    <td>
                      <div className="row gap-6">
                        {item.imageUrl ? (
                          <img className="menu-thumb" src={item.imageUrl} alt="" loading="lazy" />
                        ) : (
                          <span className="menu-thumb blank" aria-hidden="true">
                            ▨
                          </span>
                        )}
                        <span className={`veg-dot ${item.isVegetarian ? '' : 'nonveg'}`} />
                        <span className="b">{item.name}</span>
                      </div>
                      <div className="xs muted mono">
                        {item.sku} · {item.station.toLowerCase()} · GST {item.gstRate}%
                      </div>
                    </td>
                    <td className="dim">{item.category}</td>
                    <td className="num b">
                      {item.price > 0 ? (
                        money0(item.price)
                      ) : (
                        <span className="muted">Not priced</span>
                      )}
                    </td>
                    <td className="num">
                      {item.discountPct > 0 ? (
                        <span className="chip warn">{pct(item.discountPct)}</span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="num">
                      {item.plateCost > 0 ? (
                        money(item.plateCost)
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="num">
                      {item.price > 0 ? (
                        <span
                          className={`chip ${
                            item.foodCostPct > target + 8
                              ? 'danger'
                              : item.foodCostPct > target
                                ? 'warn'
                                : 'ok'
                          }`}
                        >
                          {pct(item.foodCostPct)}
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="num">
                      {item.price > 0 ? (
                        <>
                          {money0(item.margin)}
                          <div className="xs muted">{pct(item.marginPct)}</div>
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td
                      className={`num ${item.price > 0 && Math.abs(item.delta) >= 5 ? 'b' : 'dim'}`}
                    >
                      {money0(item.suggestedPrice)}
                      {item.price > 0 && Math.abs(item.delta) >= 5 ? (
                        <div className={`xs ${item.delta > 0 ? 'warn' : 'dim'}`}>
                          {item.delta > 0 ? '▲' : '▼'} {money0(Math.abs(item.delta))}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <StatusChip status={item.flag} />
                    </td>
                    <td>
                      <span className={`chip ${item.isActive ? 'ok' : ''}`}>
                        {item.isActive ? 'On sale' : 'Off'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openId ? <ItemDrawer id={openId} target={target} onClose={() => setOpenId(null)} /> : null}
      {bulk ? (
        <BulkRepriceModal
          target={target}
          categories={categories.data ?? []}
          onClose={() => setBulk(false)}
          onDone={() => {
            setBulk(false);
            void queryClient.invalidateQueries({ queryKey: ['menu'] });
          }}
        />
      ) : null}
      {addingSection ? (
        <SectionModal
          existing={(categories.data ?? []).map((c) => c.name)}
          sections={categories.data ?? []}
          onClose={() => setAddingSection(false)}
          onDone={() => {
            setAddingSection(false);
            void categories.refetch();
          }}
        />
      ) : null}

      {starter ? (
        <StarterMenuModal
          onClose={() => {
            setStarter(false);
            setOfferedStarter(true);
          }}
          onDone={() => {
            setStarter(false);
            setOfferedStarter(true);
            void categories.refetch();
            void queryClient.invalidateQueries({ queryKey: ['menu'] });
          }}
        />
      ) : null}

      {creating ? (
        <NewItemModal
          categories={categories.data ?? []}
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['menu'] });
          }}
        />
      ) : null}
    </div>
  );
}

function ItemDrawer({ id, target, onClose }: { id: string; target: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { fail, push } = useToast();
  const { can } = useAuth();
  const [price, setPrice] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [edit, setEdit] = useState<EditForm | null>(null);
  const [recipe, setRecipe] = useState<Array<{ ingredientId: string; quantity: number }> | null>(
    null,
  );

  const item = useQuery({
    queryKey: ['menu', id],
    queryFn: () => api.get<MenuDetail>(`/api/menu/${id}`),
  });
  const ingredients = useQuery({
    queryKey: ['ingredients'],
    queryFn: () => api.get<Ingredient[]>('/api/ingredients'),
  });
  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/api/categories'),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['menu'] });
    void queryClient.invalidateQueries({ queryKey: ['menu', id] });
  };

  const savePrice = useMutation({
    mutationFn: (input: { price: number; priceChangeReason: string }) =>
      api.patch(`/api/menu/${id}`, input),
    onSuccess: () => {
      push('Price revised and logged', 'ok');
      setPrice(null);
      setReason('');
      invalidate();
    },
    onError: fail,
  });

  const saveDetails = useMutation({
    mutationFn: (input: EditForm) => api.patch(`/api/menu/${id}`, input),
    onSuccess: () => {
      push('Item updated', 'ok');
      setEdit(null);
      invalidate();
    },
    onError: fail,
  });

  /**
   * On sale is its own action, not a field in the form.
   *
   * Switching an item on is the moment it can be sold, so it is deliberately a
   * separate click - and the server refuses it while the price is zero whether
   * or not this button was disabled.
   */
  const setActive = useMutation({
    mutationFn: (isActive: boolean) => api.patch(`/api/menu/${id}`, { isActive }),
    onSuccess: (_result, isActive) => {
      push(isActive ? 'Item is on sale' : 'Item taken off sale', 'ok');
      invalidate();
    },
    onError: fail,
  });

  const saveRecipe = useMutation({
    mutationFn: (components: Array<{ ingredientId: string; quantity: number }>) =>
      api.put(`/api/menu/${id}/recipe`, { components }),
    onSuccess: () => {
      push('Recipe saved — costs recalculated', 'ok');
      setRecipe(null);
      invalidate();
    },
    onError: fail,
  });

  const data = item.data;
  const form: EditForm | null = data
    ? (edit ?? {
        name: data.name,
        categoryId: data.categoryId,
        station: data.station,
        isVegetarian: data.isVegetarian,
        gstRate: data.gstRate,
        hsn: data.hsn,
        discountPct: data.discountPct,
      })
    : null;

  const lines =
    recipe ??
    data?.recipe.map((r) => ({ ingredientId: r.ingredientId, quantity: r.quantity })) ??
    [];

  return (
    <Drawer title={data?.name ?? 'Menu item'} size="wide" onClose={onClose}>
      {item.isLoading || !data ? (
        <Loading />
      ) : (
        <div className="stack">
          <div className="kpis">
            <Kpi
              label="Selling price"
              value={money0(data.price)}
              foot={`GST ${data.gstRate}% on top`}
            />
            <Kpi
              label="Plate cost"
              value={money(data.plateCost)}
              foot={`${data.recipe.length} ingredient(s)`}
            />
            <Kpi
              label="Food cost"
              value={pct(data.foodCostPct)}
              foot={`Margin ${money0(data.margin)}`}
              tone={data.flag === 'LOW_MARGIN' ? 'red' : ''}
            />
            <Kpi
              label="Suggested"
              value={money0(data.suggestedPrice)}
              foot={`At ${pct(target)} food cost`}
              tone="amber"
            />
          </div>

          {can('menu:manage') && form ? (
            <div className="card">
              <div className="card-head">
                <h2>Item details</h2>
                <span className={`chip ${data.isActive ? 'ok' : ''}`}>
                  {data.isActive ? 'On sale' : 'Off sale'}
                </span>
              </div>
              <div className="card-body col gap-16">
                <div className="form-grid">
                  <TextInput
                    className="span2"
                    label="Name"
                    value={form.name}
                    onChange={(e) => setEdit({ ...form, name: e.target.value })}
                  />
                  <Select
                    label="Category"
                    value={form.categoryId}
                    onChange={(e) => setEdit({ ...form, categoryId: e.target.value })}
                  >
                    {[...(categories.data ?? [])]
                      .sort(compareByCategory((c) => c.name))
                      .map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                  </Select>
                  <Select
                    label="Kitchen station"
                    hint="Where the ticket prints"
                    value={form.station}
                    onChange={(e) => setEdit({ ...form, station: e.target.value })}
                  >
                    {STATIONS.map((station) => (
                      <option key={station} value={station}>
                        {titleCase(station)}
                      </option>
                    ))}
                  </Select>
                  <Select
                    label="Food type"
                    value={form.isVegetarian ? 'veg' : 'nonveg'}
                    onChange={(e) => setEdit({ ...form, isVegetarian: e.target.value === 'veg' })}
                  >
                    <option value="veg">Vegetarian</option>
                    <option value="nonveg">Non-vegetarian</option>
                  </Select>
                  <Select
                    label="GST rate"
                    value={String(form.gstRate)}
                    onChange={(e) => setEdit({ ...form, gstRate: Number(e.target.value) })}
                  >
                    <option value="0">0% — exempt (unbranded staples, some dairy)</option>
                    <option value="5">5% — restaurant service</option>
                    <option value="12">12%</option>
                    <option value="18">18%</option>
                    <option value="28">28%</option>
                  </Select>
                  <TextInput
                    label="Discount %"
                    type="number"
                    min={0}
                    max={100}
                    step="0.5"
                    hint={
                      form.discountPct > 0 && data.price > 0
                        ? `Sells at ${money(data.price * (1 - form.discountPct / 100))} before GST`
                        : 'Taken off every bill line, before GST'
                    }
                    value={form.discountPct}
                    onChange={(e) => setEdit({ ...form, discountPct: Number(e.target.value) || 0 })}
                  />
                  <TextInput
                    label="HSN / SAC"
                    hint="996331 for restaurant service"
                    value={form.hsn}
                    onChange={(e) => setEdit({ ...form, hsn: e.target.value })}
                  />
                </div>

                <div className="row between wrap gap-6">
                  <div className="row gap-6">
                    <button
                      className="btn primary"
                      disabled={!edit || !form.name.trim() || saveDetails.isPending}
                      onClick={() => saveDetails.mutate(form)}
                    >
                      {saveDetails.isPending ? 'Saving…' : 'Save changes'}
                    </button>
                    {edit ? (
                      <button className="btn" onClick={() => setEdit(null)}>
                        Cancel
                      </button>
                    ) : null}
                  </div>

                  <div className="row gap-6">
                    {data.isActive ? (
                      <button
                        className="btn"
                        disabled={setActive.isPending}
                        onClick={() => setActive.mutate(false)}
                      >
                        Take off sale
                      </button>
                    ) : (
                      <button
                        className="btn primary"
                        disabled={data.price <= 0 || setActive.isPending}
                        title={
                          data.price <= 0 ? 'Set a price above zero first' : 'Put this item on sale'
                        }
                        onClick={() => setActive.mutate(true)}
                      >
                        Put on sale
                      </button>
                    )}
                  </div>
                </div>

                {data.price <= 0 ? (
                  <p className="small warn">
                    This item has no price yet, so it cannot go on sale. Set one below and it
                    becomes sellable.
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          {can('menu:price') ? (
            <div className="card">
              <div className="card-head">
                <h2>Revise price</h2>
              </div>
              <div className="card-body col gap-16">
                <div className="form-grid">
                  <TextInput
                    label="New price (₹)"
                    type="number"
                    value={price ?? data.price}
                    onChange={(e) => setPrice(Number(e.target.value) || 0)}
                  />
                  <TextInput
                    label="Reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. input cost increase"
                  />
                </div>
                <div className="row gap-6">
                  <button
                    className="btn primary"
                    disabled={savePrice.isPending}
                    onClick={() =>
                      savePrice.mutate({
                        price: price ?? data.price,
                        priceChangeReason: reason || 'Manual revision',
                      })
                    }
                  >
                    Apply price
                  </button>
                  <button
                    className="btn"
                    disabled={savePrice.isPending || data.plateCost === 0}
                    onClick={() =>
                      savePrice.mutate({
                        price: data.suggestedPrice,
                        priceChangeReason: `Aligned to ${target}% food cost`,
                      })
                    }
                  >
                    Use suggested {money0(data.suggestedPrice)}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <MenuItemImage
            itemId={id}
            imageUrl={data.imageUrl}
            name={data.name}
            canEdit={can('menu:manage')}
          />

          <div className="card">
            <div className="card-head">
              <h2>Recipe</h2>
              {can('menu:manage') ? (
                <button
                  className="btn sm"
                  onClick={() => setRecipe([...lines, { ingredientId: '', quantity: 0 }])}
                >
                  + Ingredient
                </button>
              ) : null}
            </div>
            <div className="card-body">
              {lines.length === 0 ? (
                <Empty message="No recipe yet — this item cannot be costed" />
              ) : (
                <div className="col gap-6">
                  {lines.map((line, index) => (
                    <div className="row gap-6" key={index}>
                      <select
                        className="select"
                        style={{ flex: 2 }}
                        value={line.ingredientId}
                        disabled={!can('menu:manage')}
                        onChange={(e) => {
                          const next = [...lines];
                          next[index] = { ...line, ingredientId: e.target.value };
                          setRecipe(next);
                        }}
                      >
                        <option value="">— select ingredient —</option>
                        {(ingredients.data ?? []).map((ingredient) => (
                          <option key={ingredient.id} value={ingredient.id}>
                            {ingredient.name} ({ingredient.unit.toLowerCase()}) ·{' '}
                            {money(ingredient.avgCost)}
                          </option>
                        ))}
                      </select>
                      <input
                        className="input"
                        type="number"
                        step="0.001"
                        style={{ width: 110 }}
                        value={line.quantity}
                        disabled={!can('menu:manage')}
                        onChange={(e) => {
                          const next = [...lines];
                          next[index] = { ...line, quantity: Number(e.target.value) || 0 };
                          setRecipe(next);
                        }}
                      />
                      {can('menu:manage') ? (
                        <button
                          className="btn ghost icon sm"
                          onClick={() => setRecipe(lines.filter((_, i) => i !== index))}
                        >
                          ✕
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}

              {recipe && can('menu:manage') ? (
                <div className="row gap-6" style={{ marginTop: 12 }}>
                  <button
                    className="btn primary sm"
                    disabled={saveRecipe.isPending}
                    onClick={() =>
                      saveRecipe.mutate(recipe.filter((l) => l.ingredientId && l.quantity > 0))
                    }
                  >
                    Save recipe
                  </button>
                  <button className="btn sm" onClick={() => setRecipe(null)}>
                    Cancel
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          {data.recipe.length > 0 ? (
            <div className="card">
              <div className="card-head">
                <h2>Cost breakdown</h2>
              </div>
              <div className="table-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Ingredient</th>
                      <th className="num">Quantity</th>
                      <th className="num">Unit cost</th>
                      <th className="num">Line cost</th>
                      <th className="num" style={{ width: 130 }}>
                        Share
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recipe.map((line) => (
                      <tr key={line.ingredientId}>
                        <td>{line.name}</td>
                        <td className="num">{qty(line.quantity, line.unit)}</td>
                        <td className="num dim">{money(line.unitCost)}</td>
                        <td className="num b">{money(line.lineCost)}</td>
                        <td>
                          <Meter
                            value={data.plateCost ? (line.lineCost / data.plateCost) * 100 : 0}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3}>Total plate cost</td>
                      <td className="num">{money(data.plateCost)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ) : null}

          <div className="card">
            <div className="card-head">
              <h2>Price history</h2>
            </div>
            {data.priceHistory.length === 0 ? (
              <Empty message="No revisions yet" />
            ) : (
              <div className="table-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th className="num">From</th>
                      <th className="num">To</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.priceHistory.map((entry) => (
                      <tr key={entry.id}>
                        <td>{date(entry.changedAt)}</td>
                        <td className="num dim">{money0(entry.oldPrice)}</td>
                        <td className="num b">{money0(entry.newPrice)}</td>
                        <td className="small">{entry.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}

function BulkRepriceModal({
  target,
  categories,
  onClose,
  onDone,
}: {
  target: number;
  categories: Category[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { fail, push } = useToast();
  const [mode, setMode] = useState<'TARGET_FOOD_COST' | 'UPLIFT'>('TARGET_FOOD_COST');
  const [categoryId, setCategoryId] = useState('');
  const [targetPct, setTargetPct] = useState(target);
  const [uplift, setUplift] = useState(5);
  const [confirming, setConfirming] = useState(false);

  const apply = useMutation({
    mutationFn: () =>
      api.post<{ changed: number }>('/api/pricing/bulk', {
        mode,
        categoryId: categoryId || undefined,
        targetFoodCostPct: mode === 'TARGET_FOOD_COST' ? targetPct : undefined,
        upliftPct: mode === 'UPLIFT' ? uplift : undefined,
        reason:
          mode === 'UPLIFT' ? `Bulk uplift ${uplift}%` : `Repriced to ${targetPct}% food cost`,
      }),
    onSuccess: (result) => {
      push(`${result.changed} item(s) repriced`, 'ok');
      onDone();
    },
    onError: (error) => {
      setConfirming(false);
      fail(error);
    },
  });

  if (confirming) {
    return (
      <ConfirmDialog
        message={`Reprice ${categoryId ? categories.find((c) => c.id === categoryId)?.name : 'the whole menu'} using ${
          mode === 'UPLIFT' ? `a ${uplift}% uplift` : `${targetPct}% food cost`
        }? Every change is written to price history.`}
        confirmLabel="Reprice"
        busy={apply.isPending}
        onConfirm={() => apply.mutate()}
        onCancel={() => setConfirming(false)}
      />
    );
  }

  return (
    <Modal
      title="Bulk reprice"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => setConfirming(true)}>
            Review
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <p className="dim small">
          Prices round up to the nearest ₹5. Items without a recipe are skipped when repricing to a
          food-cost target.
        </p>
        <div className="form-grid">
          <Select
            label="Category"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">All categories</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
          <Select
            label="Method"
            value={mode}
            onChange={(e) => setMode(e.target.value as typeof mode)}
          >
            <option value="TARGET_FOOD_COST">Align to target food cost</option>
            <option value="UPLIFT">Flat percentage uplift</option>
          </Select>
          {mode === 'TARGET_FOOD_COST' ? (
            <TextInput
              label="Target food cost %"
              type="number"
              value={targetPct}
              onChange={(e) => setTargetPct(Number(e.target.value) || target)}
            />
          ) : (
            <TextInput
              label="Uplift %"
              type="number"
              value={uplift}
              onChange={(e) => setUplift(Number(e.target.value) || 0)}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

function NewItemModal({
  categories,
  onClose,
  onDone,
}: {
  categories: Category[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { fail, push } = useToast();
  // A new item starts free and off sale: it is created before anyone has costed
  // it, and the server will not let a zero-priced item go on sale anyway.
  const [form, setForm] = useState({
    name: '',
    categoryId: categories[0]?.id ?? '',
    price: 0,
    // Zero until somebody picks the slab: a rate is a decision about the dish,
    // and a pre-filled 5% was being saved without anyone having made it.
    gstRate: 0,
    station: 'KITCHEN',
    isVegetarian: true,
    hsn: '996331',
    discountPct: 0,
    isActive: false,
  });

  const create = useMutation({
    mutationFn: () => api.post('/api/menu', form),
    onSuccess: () => {
      push(
        form.price > 0
          ? 'Item created — put it on sale once you are happy with the price'
          : 'Item created, unpriced and off sale',
        'ok',
      );
      onDone();
    },
    onError: fail,
  });

  return (
    <Modal
      title="New menu item"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!form.name.trim() || !form.categoryId || create.isPending}
            onClick={() => create.mutate()}
          >
            Create item
          </button>
        </>
      }
    >
      <div className="form-grid">
        <TextInput
          className="span2"
          label="Name *"
          autoFocus
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <Select
          label="Category"
          value={form.categoryId}
          onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
        >
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </Select>
        <TextInput
          label="Price (₹, pre-GST)"
          type="number"
          min={0}
          hint="Leave at 0 and price it later — it stays off sale until you do"
          value={form.price}
          onChange={(e) => setForm({ ...form, price: Number(e.target.value) || 0 })}
        />
        <TextInput
          label="Discount %"
          type="number"
          min={0}
          max={100}
          step="0.5"
          hint="Taken off every bill line, before GST"
          value={form.discountPct}
          onChange={(e) => setForm({ ...form, discountPct: Number(e.target.value) || 0 })}
        />
        <Select
          label="GST rate"
          value={String(form.gstRate)}
          onChange={(e) => setForm({ ...form, gstRate: Number(e.target.value) })}
        >
          <option value="0">0% — exempt (unbranded staples, some dairy)</option>
          <option value="5">5% — restaurant service</option>
          <option value="12">12%</option>
          <option value="18">18%</option>
          <option value="28">28%</option>
        </Select>
        <Select
          label="Kitchen station"
          value={form.station}
          onChange={(e) => setForm({ ...form, station: e.target.value })}
        >
          {['KITCHEN', 'GRILL', 'FRYER', 'OVEN', 'BEVERAGE', 'BAKERY'].map((station) => (
            <option key={station} value={station}>
              {station[0] + station.slice(1).toLowerCase()}
            </option>
          ))}
        </Select>
        <Select
          label="Food type"
          value={form.isVegetarian ? 'veg' : 'nonveg'}
          onChange={(e) => setForm({ ...form, isVegetarian: e.target.value === 'veg' })}
        >
          <option value="veg">Vegetarian</option>
          <option value="nonveg">Non-vegetarian</option>
        </Select>
      </div>
    </Modal>
  );
}

/**
 * A new section on the menu card.
 *
 * The standard sections are offered first, because a hotel that types its own
 * "Starter" alongside our "Starters" ends up with two of everything in the
 * sales report - and because where a section sits on the card is then decided
 * for them. Anything genuinely their own is still allowed, and lands after the
 * standard ones.
 */
function SectionModal({
  existing,
  sections,
  onClose,
  onDone,
}: {
  existing: string[];
  sections: Category[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { fail, push } = useToast();
  const taken = new Set(existing.map((name) => name.trim().toLowerCase()));
  const available = MENU_SECTIONS.filter((section) => !taken.has(section.name.toLowerCase()));

  const [choice, setChoice] = useState(available[0]?.name ?? '__other__');
  const [custom, setCustom] = useState('');
  const name = choice === '__other__' ? custom.trim() : choice;

  const create = useMutation({
    mutationFn: () => api.post('/api/categories', { name }),
    onSuccess: () => {
      push(`${name} added`, 'ok');
      onDone();
    },
    onError: fail,
  });

  const hint = MENU_SECTIONS.find((section) => section.name === choice)?.hint;

  return (
    <Modal
      title="Add a menu section"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!name || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Adding…' : 'Add section'}
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <Select
          label="Section"
          hint={hint ?? 'Sections sort in menu-card order wherever they appear'}
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
        >
          {available.map((section) => (
            <option key={section.name} value={section.name}>
              {section.name}
            </option>
          ))}
          <option value="__other__">Something else…</option>
        </Select>

        {choice === '__other__' ? (
          <TextInput
            label="What do you call it?"
            placeholder="Chef's Specials"
            hint="Your own sections sort after the standard ones"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
          />
        ) : null}

        {available.length === 0 && choice !== '__other__' ? (
          <p className="small muted">
            You already have every standard section — add one of your own instead.
          </p>
        ) : null}

        {sections.length > 0 ? (
          <>
            <hr className="rule" />
            <div className="col gap-6">
              <span className="small b">Sections you have</span>
              {/*
               * Editable here because this is where somebody looks when a
               * section is wrong. They could be created and never changed, so
               * a typo made on the first afternoon was permanent and a hotel
               * that reorganised its card had no way to say so.
               */}
              {sections.map((section) => (
                <SectionRow key={section.id} section={section} onDone={onDone} />
              ))}
              <span className="xs muted">
                Renaming a section moves every dish in it. A section has to be empty before it can
                go — move its dishes first, from the item itself.
              </span>
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  );
}

/** One section, with the two things anybody wants to do to it. */
function SectionRow({ section, onDone }: { section: Category; onDone: () => void }) {
  const { fail, push } = useToast();
  const [name, setName] = useState(section.name);
  const dirty = name.trim() !== section.name && name.trim().length > 0;

  const rename = useMutation({
    mutationFn: () => api.patch(`/api/categories/${section.id}`, { name: name.trim() }),
    onSuccess: () => {
      push(`Renamed to ${name.trim()}`, 'ok');
      onDone();
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/api/categories/${section.id}`),
    onSuccess: () => {
      push(`${section.name} removed`, 'ok');
      onDone();
    },
    // The server refuses while dishes are in it and says how many, which is
    // more use than anything this could guess at.
    onError: fail,
  });

  return (
    <div className="row gap-6">
      <input
        className="input"
        value={name}
        aria-label={`Rename ${section.name}`}
        onChange={(e) => setName(e.target.value)}
      />
      <button
        className="btn sm"
        disabled={!dirty || rename.isPending}
        onClick={() => rename.mutate()}
      >
        {rename.isPending ? 'Saving…' : 'Rename'}
      </button>
      <button className="btn sm danger" disabled={remove.isPending} onClick={() => remove.mutate()}>
        {remove.isPending ? 'Removing…' : 'Remove'}
      </button>
    </div>
  );
}

interface StarterItem {
  name: string;
  category: string;
  station: string;
  isVegetarian: boolean;
  added: boolean;
}

/**
 * The starter catalogue, to pick from.
 *
 * Registration used to create all of this without asking, so a new hotel's
 * first job was working through somebody else's menu deleting what it does
 * not sell - and a kitchen that serves no meat had been handed meat dishes,
 * which is not a neutral default to be corrected later.
 *
 * The list is filtered by what the hotel told us it serves, so a vegetarian
 * kitchen is never shown one. Nothing is ticked to begin with: this is an
 * offer, and an owner who wants to write their own menu should be able to
 * close it and have nothing happen.
 */
function StarterMenuModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { fail, push } = useToast();
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const catalogue = useQuery({
    queryKey: ['menu', 'starter'],
    queryFn: () =>
      api.get<{ foodType: string; items: StarterItem[] }>('/api/menu/starter/catalogue'),
  });

  const available = (catalogue.data?.items ?? []).filter((item) => !item.added);
  const byCategory = useMemo(() => {
    const groups = new Map<string, StarterItem[]>();
    for (const item of available) {
      const list = groups.get(item.category) ?? [];
      list.push(item);
      groups.set(item.category, list);
    }
    return [...groups.entries()].sort(([a], [b]) => compareByCategory((x: string) => x)(a, b));
  }, [available]);

  const toggle = (name: string) =>
    setPicked((previous) => {
      const next = new Set(previous);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const add = useMutation({
    mutationFn: () => api.post<{ created: number }>('/api/menu/starter', { names: [...picked] }),
    onSuccess: (result) => {
      push(`${result.created} dish${result.created === 1 ? '' : 'es'} added`, 'ok');
      onDone();
    },
    onError: fail,
  });

  return (
    <Modal
      title="Add dishes from the starter menu"
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Not now
          </button>
          <button
            className="btn primary"
            disabled={picked.size === 0 || add.isPending}
            onClick={() => add.mutate()}
          >
            {add.isPending
              ? 'Adding…'
              : `Add ${picked.size || ''} ${picked.size === 1 ? 'dish' : 'dishes'}`.trim()}
          </button>
        </>
      }
    >
      {catalogue.isLoading ? (
        <Loading />
      ) : available.length === 0 ? (
        <Empty message="Every dish on the starter list is already on your menu." />
      ) : (
        <div className="col gap-16">
          <p className="form-note info">
            <b>Tick what your kitchen actually serves.</b> Each one arrives with its recipe, priced
            at zero and off sale — set a price when you are ready, and nothing can be sold until you
            do. You can come back to this list any time.
          </p>

          {byCategory.map(([category, items]) => {
            const names = items.map((item) => item.name);
            const allPicked = names.every((name) => picked.has(name));
            return (
              <div key={category} className="col gap-6">
                <div className="row between">
                  <span className="small b">{category}</span>
                  {/* A section at a time, because that is how somebody
                      decides: "we do all the breads, none of the pizzas". */}
                  <button
                    className="btn ghost sm"
                    onClick={() =>
                      setPicked((previous) => {
                        const next = new Set(previous);
                        for (const name of names) {
                          if (allPicked) next.delete(name);
                          else next.add(name);
                        }
                        return next;
                      })
                    }
                  >
                    {allPicked ? 'None' : 'All'}
                  </button>
                </div>
                <div className="starter-grid">
                  {items.map((item) => (
                    <label key={item.name} className="starter-pick">
                      <input
                        type="checkbox"
                        checked={picked.has(item.name)}
                        onChange={() => toggle(item.name)}
                      />
                      <span className={`veg-dot ${item.isVegetarian ? '' : 'nonveg'}`} />
                      <span className="grow">{item.name}</span>
                      <span className="xs muted">{item.station.toLowerCase()}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
